import { Matrix, PhaseValidator } from '../core';
import { MatrixValuesCommands } from '../core/context/matrix-value/commands';
import { type MatrixValues } from '../core/context/matrix-value/repository';
import { TraBase, type TraBaseConfig } from '../core/tra-base';
import { assetRelationTypes } from './asset-relation-types';
import { attackerTypes } from './attacker-types';
import { AssetProtectionGoalImpactsCommands } from './context/asset-protection-goal-impact/commands';
import { AssetProtectionGoalImpacts } from './context/asset-protection-goal-impact/repository';
import { AssetRelationTypesCommands } from './context/asset-relation-type/commands';
import { AssetRelationTypes } from './context/asset-relation-type/repository';
import { AssetRelationsCommands } from './context/asset-relation/commands';
import { type AssetRelationData } from './context/asset-relation/dto';
import { AssetRelations } from './context/asset-relation/repository';
import { AssetsCommands } from './context/asset/commands';
import { type AssetData } from './context/asset/dto';
import { Assets } from './context/asset/repository';
import { AssumptionCommands } from './context/assumption/commands';
import { Assumptions } from './context/assumption/repository';
import { AttackerTypesCommands } from './context/attacker-type/commands';
import { AttackerTypes } from './context/attacker-type/repository';
import { CustomAttributeCommands } from './context/custom-attribute/commands';
import { type CustomAttributeData } from './context/custom-attribute/dto';
import { CustomAttributes } from './context/custom-attribute/repository';
import { ProtectionGoalsCommands } from './context/protection-goal/commands';
import { ProtectionGoals } from './context/protection-goal/repository';
import { ResponsiblesCommands } from './context/responsible/commands';
import { Responsibles } from './context/responsible/repository';
import { ScopeCommands } from './context/scope/commands';
import { type Scopes } from './context/scope/repository';
import { ThreatPathsCommands } from './context/threat-path/commands';
import { ThreatPaths } from './context/threat-path/repository';
import { ThreatVectorCommentsCommands } from './context/threat-vector-comment/commands';
import { ThreatVectorComments } from './context/threat-vector-comment/repository';
import { ThreatVectorMitigationTasksCommands } from './context/threat-vector-mitigation-task/commands';
import { ThreatVectorMitigationTasks } from './context/threat-vector-mitigation-task/repository';
import { ThreatVectorMitigationsCommands } from './context/threat-vector-mitigation/commands';
import { ThreatVectorMitigations } from './context/threat-vector-mitigation/repository';
import { ThreatVectorsCommands } from './context/threat-vector/commands';
import { ThreatVectors } from './context/threat-vector/repository';
import { ZonesCommands } from './context/zone/commands';
import { type ZoneData } from './context/zone/dto';
import { Zones } from './context/zone/repository';
import {
    likelihoodMatrix,
    MatrixValueGroup,
    matrixValues,
    riskMatrix,
} from './matrixes';
import { phaseCompleteRules } from './phase-complete-rules';
import { phaseLockRules } from './phase-lock-rules';
import { phases } from './phases';
import { protectionGoals } from './protection-goals';
import { responsibleList } from './responsible';
import { schema } from './schema';
import { scopesList } from './scopes';
import { type TraV1BodyData } from './types';

const traV1Config: TraBaseConfig = {
    version: 1,
    schema,
    phases,
    phaseLockRules,
};

export class TraV1 extends TraBase {
    documentID?: string;
    zones: Zones;
    assets: Assets;
    assetRelations: AssetRelations;
    assetRelationTypes: AssetRelationTypes;
    threatPaths: ThreatPaths;
    threatVectors: ThreatVectors;
    protectionGoals: ProtectionGoals;
    attackerTypes: AttackerTypes;
    assetProtectionGoalImpacts: AssetProtectionGoalImpacts;
    threatVectorMitigations: ThreatVectorMitigations;
    threatVectorMitigationTasks: ThreatVectorMitigationTasks;
    threatVectorComments: ThreatVectorComments;
    responsibles: Responsibles;
    matrixValues: MatrixValues;
    scopes: Scopes;
    assumptions: Assumptions;
    customAttributes: CustomAttributes;

    riskMatrix: Matrix;
    likelihoodMatrix: Matrix;

    // eslint-disable-next-line @typescript-eslint/no-empty-function -- by default callback function can do nothing
    constructor(protected changeCallback: () => void = () => {}) {
        super(traV1Config, changeCallback);
        this.phaseValidator = new PhaseValidator(phaseCompleteRules, this);
        this.zones = new Zones(changeCallback);
        this.assets = new Assets(changeCallback);
        this.assetRelations = new AssetRelations(changeCallback);
        this.assetRelationTypes = new AssetRelationTypes(changeCallback);
        this.threatPaths = new ThreatPaths(changeCallback);
        this.threatVectors = new ThreatVectors(changeCallback);
        this.protectionGoals = new ProtectionGoals(changeCallback);
        this.attackerTypes = new AttackerTypes(changeCallback);
        this.responsibles = new Responsibles(changeCallback);
        this.assetProtectionGoalImpacts = new AssetProtectionGoalImpacts(
            changeCallback,
        );
        this.threatVectorMitigations = new ThreatVectorMitigations(
            changeCallback,
        );
        this.threatVectorMitigationTasks = new ThreatVectorMitigationTasks(
            changeCallback,
        );
        this.threatVectorComments = new ThreatVectorComments(changeCallback);
        this.assumptions = new Assumptions(changeCallback);
        this.customAttributes = new CustomAttributes(changeCallback);

        this.scopes = new ScopeCommands().fromJSON(scopesList, changeCallback);

        this.matrixValues = new MatrixValuesCommands().fromJSON(
            matrixValues,
            this.changeCallback,
        );
        this.protectionGoals = new ProtectionGoalsCommands().fromJSON(
            protectionGoals,
            this.changeCallback,
        );

        this.attackerTypes = new AttackerTypesCommands().fromJSON(
            attackerTypes,
            this.changeCallback,
        );

        this.assetRelationTypes = new AssetRelationTypesCommands().fromJSON(
            assetRelationTypes,
            this.changeCallback,
        );

        this.responsibles = new ResponsiblesCommands().fromJSON(
            responsibleList,
            this.changeCallback,
        );

        this.likelihoodMatrix = new Matrix(likelihoodMatrix);
        this.riskMatrix = new Matrix(riskMatrix);
    }

    parse(data: Record<string, unknown>): void {
        super.parse(data);
        if (this.error) {
            return;
        }
        const validData = data as unknown as TraV1BodyData;
        this._error = null;
        this.documentID = validData.documentID?.toString();
        this.matrixValues = new MatrixValuesCommands().fromJSON(
            validData.matrixValues?.length
                ? validData.matrixValues
                : matrixValues,
            this.changeCallback,
        );
        this.likelihoodMatrix = new Matrix(
            validData.matrix.likelihood ?? likelihoodMatrix,
        );
        this.riskMatrix = new Matrix(validData.matrix.risk ?? riskMatrix);
        this.zones = new ZonesCommands().fromJSON(
            validData.zones ?? [],
            this.changeCallback,
        );
        this.assets = new AssetsCommands().fromJSON(
            validData.assets ?? [],
            this.changeCallback,
        );
        this.protectionGoals = new ProtectionGoalsCommands().fromJSON(
            validData.protectionGoals ?? [],
            this.changeCallback,
        );
        this.attackerTypes = new AttackerTypesCommands().fromJSON(
            validData.attackerTypes ?? [],
            this.changeCallback,
        );

        this.assetRelationTypes = new AssetRelationTypesCommands().fromJSON(
            validData.assetRelationTypes?.length
                ? validData.assetRelationTypes
                : assetRelationTypes,
            this.changeCallback,
        );
        this.assetRelations = new AssetRelationsCommands().fromJSON(
            validData.assetRelations ?? [],
            this.changeCallback,
        );
        this.threatVectors = new ThreatVectorsCommands().fromJSON(
            validData.threatVectors ?? [],
            this.changeCallback,
        );
        this.threatPaths = new ThreatPathsCommands().fromJSON(
            validData.threatPaths ?? [],
            this.changeCallback,
        );
        this.assetProtectionGoalImpacts =
            new AssetProtectionGoalImpactsCommands().fromJSON(
                validData.assetProtectionGoalImpacts ?? [],
                this.changeCallback,
            );
        this.threatVectorMitigations =
            new ThreatVectorMitigationsCommands().fromJSON(
                validData.threatVectorMitigations ?? [],
                this.changeCallback,
            );
        this.threatVectorMitigationTasks =
            new ThreatVectorMitigationTasksCommands().fromJSON(
                validData.threatVectorMitigationTasks ?? [],
                this.changeCallback,
            );
        this.threatVectorComments = new ThreatVectorCommentsCommands().fromJSON(
            validData.threatVectorComments ?? [],
            this.changeCallback,
        );
        this.responsibles = new ResponsiblesCommands().fromJSON(
            validData.responsibles?.length
                ? validData.responsibles
                : responsibleList,
            this.changeCallback,
        );
        this.scopes = new ScopeCommands().fromJSON(
            validData.scopes ?? [],
            this.changeCallback,
        );
        this.assumptions = new AssumptionCommands().fromJSON(
            validData.assumptions ?? [],
            this.changeCallback,
        );
        this.customAttributes = new CustomAttributeCommands().fromJSON(
            validData.customAttributes ?? [],
            this.changeCallback,
        );
    }

    setProjectData({
        zones,
        assets,
        customAttributes,
        assetRelations,
    }: {
        zones: ZoneData[];
        assets: AssetData[];
        customAttributes: CustomAttributeData[];
        assetRelations: AssetRelationData[];
    }): void {
        const exposureValues = this.matrixValues.getAllByGroup(
            MatrixValueGroup.Exposure,
        );
        const criticalityValues = this.matrixValues.getAllByGroup(
            MatrixValueGroup.Criticality,
        );
        new ZonesCommands().addAllWithMatrix(this.zones, zones, exposureValues);

        new AssetsCommands().addAllWithMatrix(
            this.assets,
            assets,
            criticalityValues,
        );
        new CustomAttributeCommands().addAll(
            this.customAttributes,
            customAttributes,
        );
        new AssetRelationsCommands().addAll(
            this.assetRelations,
            assetRelations,
        );
    }

    toJSON(): object {
        return {
            ...super.toJSON(),
            documentID: this.documentID,
            matrix: {
                likelihood: this.likelihoodMatrix.toJSON(),
                risk: this.riskMatrix.toJSON(),
            },
            zones: this.zones.toJSON(),
            assets: this.assets.toJSON(),
            assetRelations: this.assetRelations.toJSON(),
            assetRelationTypes: this.assetRelationTypes.toJSON(),
            threatVectors: this.threatVectors.toJSON(),
            threatPaths: this.threatPaths.toJSON(),
            protectionGoals: this.protectionGoals.toJSON(),
            attackerTypes: this.attackerTypes.toJSON(),
            assetProtectionGoalImpacts:
                this.assetProtectionGoalImpacts.toJSON(),
            threatVectorMitigations: this.threatVectorMitigations.toJSON(),
            threatVectorMitigationTasks:
                this.threatVectorMitigationTasks.toJSON(),
            threatVectorComments: this.threatVectorComments.toJSON(),
            responsibles: this.responsibles.toJSON(),
            matrixValues: this.matrixValues.toJSON(),
            scopes: this.scopes.toJSON(),
            assumptions: this.assumptions.toJSON(),
            customAttributes: this.customAttributes.toJSON(),
        };
    }
}
